import { Injectable } from '@angular/core';
import Hotjar from '@hotjar/browser';
import { environment } from 'src/environments/environment';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class HotjarService {
  constructor(private translocoService: TranslocoService) {}

  // CSS selectors for Hotjar survey widget
  surveyContainerSelector = '._hj-widget-container';
  // CSS selectors for close and next buttons
  closeSelector = '.css-ivhzbm';
  nextSelector = '.css-a3gvy2';

  getRoles(): string {
    const jsonRoles = localStorage.getItem('roles');
    if (jsonRoles) {
      // Prevent invalid JSON from breaking the app
      let roles;
      try {
        roles = JSON.parse(jsonRoles);
      } catch (e) {
        console.error('Hotjar ::: Invalid JSON in roles:', jsonRoles);
        return '';
      }
      return roles.map((role: { name: string }) => role.name).join(',');
    }
    return '';
  }

  openSurveyByEvent(
    eventName: string,
    attributes: any = {},
    localStorageName: string = '',
    hideClose: boolean = false
  ): void {
    const env = environment.envName;
    if (env === 'local') {
      console.log('Hotjar ::: not available in local environment');
      return;
    }

    // Initialize Hotjar
    const siteId = environment.hotjarSiteId;
    const hotjarVersion = 6;
    Hotjar.init(siteId, hotjarVersion, {
      debug: false,
    });

    // Identify user
    const userUuid = localStorage.getItem('user_uuid') || 'no_user_uuid';
    const lang = this.translocoService.getActiveLang() || 'en';
    const userInfo = {
      roles: this.getRoles(),
      timestamp: Date.now(),
      lang,
      ...attributes,
    };
    console.log(
      `Hotjar ::: identify userUuid ${userUuid}, attributes`,
      userInfo
    );
    Hotjar.identify(userUuid, userInfo);

    // Open survey
    const name = `${eventName}_${lang}_${env}`;
    console.log(`Hotjar ::: Open certification survey: ${name}`);
    Hotjar.event(name);

    // hide close button
    if (hideClose) {
      this.hideCloseButton(localStorageName);
    }
  }

  hideCloseButton(localStorageName: string = ''): void {
    // For fullscreen surveys, hide the close button
    const maxAttemptsAllowed = 50;
    let attempts = 0;
    const interval = setInterval(() => {
      const btnClose = document.querySelector(
        this.closeSelector
      ) as HTMLElement;
      attempts++;
      if (btnClose && attempts < maxAttemptsAllowed) {
        clearInterval(interval);
        console.log('Hotjar ::: Hide close button');
        btnClose.style.display = 'none';
        this.showCloseButton(localStorageName);
      }
    }, 10);
  }

  showCloseButton(localStorageName: string = ''): void {
    // For fullscreen surveys, show the close button and click it
    const interval = setInterval(() => {
      const btnClose = document.querySelector(
        this.closeSelector
      ) as HTMLElement;
      const btnNext = document.querySelector(this.nextSelector) as HTMLElement;
      if (btnClose && !btnNext) {
        clearInterval(interval);
        console.log('Hotjar ::: Trigger close');
        btnClose.click();
        this.setSurveyInLocalStorage(localStorageName);
      }
    }, 3000);
  }

  closeSurvey(): void {
    console.log('Hotjar ::: closeSurvey called, looking for selector:', this.surveyContainerSelector);
    const container = document.querySelector(this.surveyContainerSelector);
    if (container) {
      console.log('Hotjar ::: Survey container found, removing from DOM:', container);
      container.remove();
      console.log('Hotjar ::: Survey container removed successfully');
    } else {
      console.warn('Hotjar ::: Survey container not found in DOM — selector may be outdated or survey was not rendered yet');
    }
  }

  setSurveyInLocalStorage(localStorageName: string = ''): void {
    if (localStorageName) {
      console.log(`Hotjar ::: Set survey in localStorage: ${localStorageName}`);
      // Set the survey in localStorage to prevent it from showing again
      localStorage.removeItem(localStorageName);
      localStorage.setItem(localStorageName, 'true');
    }
  }
}
